import { motion } from "framer-motion";
import { Button } from "@/components/Button";
import { useRouter } from "next/router";
import { useTranslations } from 'next-intl';
import { SEOMeta } from "@/templates/SEOMeta";
import {
  RocketLaunchIcon,
  UsersIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  PuzzlePieceIcon,
  MegaphoneIcon,
  AdjustmentsHorizontalIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  BuildingStorefrontIcon,
  ComputerDesktopIcon,
  GlobeAltIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import {SeoInternalLinks} from "@/components/SeoInternalLinks";
import { getI18nProps } from '@/utils/i18n';
import { generateFAQPageSchema, generateSoftwareApplicationSchema } from '@/utils/schema';

export default function AffiliateMarketingSoftware({ seoMeta }) {
  const router = useRouter();
  const t = useTranslations('affiliateMarketingSoftware');

  const stats = [
    {
      value: t('stats.fullFunnel.value'),
      label: t('stats.fullFunnel.label'),
      icon: ChartBarIcon
    },
    {
      value: t('stats.flexibleAttribution.value'),
      label: t('stats.flexibleAttribution.label'),
      icon: AdjustmentsHorizontalIcon
    },
    {
      value: t('stats.integrationOptions.value'),
      label: t('stats.integrationOptions.label'),
      icon: PuzzlePieceIcon
    },
  ];

  const steps = [
    {
      icon: RocketLaunchIcon,
      title: t('steps.launch.title'),
      description: t('steps.launch.description')
    },
    {
      icon: UsersIcon,
      title: t('steps.onboard.title'),
      description: t('steps.onboard.description')
    },
    {
      icon: ChartBarIcon,
      title: t('steps.track.title'),
      description: t('steps.track.description')
    }
  ];

  const features = [
    {
      icon: ShieldCheckIcon,
      title: t('features.brandSafe.title'),
      description: t('features.brandSafe.description'),
      bullet: t('features.brandSafe.bullet')
    },
    {
      icon: AdjustmentsHorizontalIcon,
      title: t('features.flexibleCommission.title'),
      description: t('features.flexibleCommission.description'),
      bullet: t('features.flexibleCommission.bullet')
    },
    {
      icon: ChartBarIcon,
      title: t('features.realTimeAnalytics.title'),
      description: t('features.realTimeAnalytics.description'),
      bullet: t('features.realTimeAnalytics.bullet')
    },
    {
      icon: CurrencyDollarIcon,
      title: t('features.payoutReady.title'),
      description: t('features.payoutReady.description'),
      bullet: t('features.payoutReady.bullet')
    },
    {
      icon: MegaphoneIcon,
      title: t('features.partnerEnablement.title'),
      description: t('features.partnerEnablement.description'),
      bullet: t('features.partnerEnablement.bullet')
    },
    {
      icon: PuzzlePieceIcon,
      title: t('features.integrationOptions.title'),
      description: t('features.integrationOptions.description'),
      bullet: t('features.integrationOptions.bullet')
    }
  ];

  const useCases = [
    {
      icon: ComputerDesktopIcon,
      title: t('useCases.saas.title'),
      description: t('useCases.saas.description')
    },
    {
      icon: BuildingStorefrontIcon,
      title: t('useCases.ecommerce.title'),
      description: t('useCases.ecommerce.description')
    },
    {
      icon: GlobeAltIcon,
      title: t('useCases.b2b.title'),
      description: t('useCases.b2b.description')
    },
    {
      icon: SparklesIcon,
      title: t('useCases.creator.title'),
      description: t('useCases.creator.description')
    }
  ];

  const faqs = [
    {
      q: t('faq.whatIsSoftware.q'),
      a: t('faq.whatIsSoftware.a')
    },
    {
      q: t('faq.supportsLinks.q'),
      a: t('faq.supportsLinks.a')
    },
    {
      q: t('faq.subidTracking.q'),
      a: t('faq.subidTracking.a')
    },
    {
      q: t('faq.howIntegrate.q'),
      a: t('faq.howIntegrate.a')
    },
    {
      q: t('faq.dataUsable.q'),
      a: t('faq.dataUsable.a')
    },
    {
      q: t('faq.suitableForSaaS.q'),
      a: t('faq.suitableForSaaS.a')
    }
  ];

  return (
      <>
        <SEOMeta
            title={seoMeta?.title || t('seo.title')}
            description={seoMeta?.description || t('seo.description')}
            keywords={seoMeta?.keywords || t('seo.keywords')}
            url={t('seo.url')}
            structuredData={[
              generateSoftwareApplicationSchema({
                description: seoMeta?.description || t('seo.description'),
                url: 'https://afivio.com/affiliate-marketing-software'
              }),
              generateFAQPageSchema(faqs)
            ]}
        />

        <div className="bg-gradient-to-b from-gray-50 to-white">
          {/* Hero */}
          <section className="pt-28 pb-24 px-6 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full opacity-5">
              <div className="absolute top-24 left-10 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-blob" />
              <div className="absolute top-20 right-10 w-72 h-72 bg-green-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000" />
              <div className="absolute bottom-16 left-1/2 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000" />
            </div>

            <div className="max-w-7xl mx-auto relative z-10 text-center">
              <motion.h1
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-5xl lg:text-6xl font-bold bg-gradient-to-r from-blue-600 to-green-500 bg-clip-text text-transparent mb-6"
              >
                {t('hero.title')}
              </motion.h1>

              <motion.p
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 }}
                  className="text-xl text-gray-600 max-w-3xl mx-auto mb-10"
              >
                {t('hero.subtitle')}
              </motion.p>

              <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.14 }}
                  className="flex flex-col sm:flex-row items-center justify-center gap-4"
              >
                <Button
                    onClick={() => router.push("/signup")}
                    className="bg-gradient-to-r from-blue-600 to-green-500 text-white px-8 py-4 rounded-xl hover:opacity-90 transition-opacity"
                >
                  {t('hero.cta')}
                </Button>

                <a
                    href="/pricing"
                    className="inline-flex items-center justify-center px-8 py-4 rounded-xl border border-gray-200 text-gray-800 hover:bg-gray-50 transition-colors"
                >
                  {t('hero.viewPricing')} <ArrowRightIcon className="w-5 h-5 ml-2" />
                </a>
              </motion.div>

              <div className="mt-10 flex flex-wrap justify-center items-center gap-6 text-sm text-gray-500">
              <span className="inline-flex items-center gap-2">
                <CheckCircleIcon className="w-5 h-5 text-green-500" /> {t('hero.gdpr')}
              </span>
                <span className="inline-flex items-center gap-2">
                <CheckCircleIcon className="w-5 h-5 text-green-500" /> {t('hero.links')}
              </span>
                <span className="inline-flex items-center gap-2">
                <CheckCircleIcon className="w-5 h-5 text-green-500" /> {t('hero.integration')}
              </span>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {stats.map((s, idx) => (
                    <motion.div
                        key={s.label}
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.08 }}
                        className="p-8 rounded-2xl bg-gray-50 border border-gray-100"
                    >
                      <s.icon className="w-10 h-10 text-blue-600 mb-4" />
                      <div className="text-xl font-semibold mb-1">{s.value}</div>
                      <div className="text-gray-600">{s.label}</div>
                    </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Steps */}
          <section className="py-24 bg-gray-50">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-14">
                <h2 className="text-4xl font-bold mb-4">{t('steps.title')}</h2>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  {t('steps.subtitle')}
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                {steps.map((step, idx) => (
                    <motion.div
                        key={step.title}
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100"
                    >
                      <div className="flex items-center mb-5">
                        <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mr-4">
                          <step.icon className="w-7 h-7 text-blue-600" />
                        </div>
                        <h3 className="text-2xl font-semibold">{step.title}</h3>
                      </div>
                      <p className="text-gray-600">{step.description}</p>
                    </motion.div>
                ))}
              </div>

              <div className="mt-10 text-center text-sm text-gray-500">
                {t('steps.detailButton1')}{" "}
                <a href="/how-it-works" className="text-blue-700 hover:underline">
                  {t('steps.detailButton2')}
                </a>
                .
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="py-24 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-14">
                <h2 className="text-4xl font-bold mb-4">{t('features.title')}</h2>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  {t('features.subtitle')}
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-10">
                {features.map((f, idx) => (
                    <motion.div
                        key={f.title}
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.06 }}
                        className="flex"
                    >
                      <div className="mr-6">
                        <f.icon className="w-12 h-12 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-semibold mb-2">{f.title}</h3>
                        <p className="text-gray-600 mb-4">{f.description}</p>
                        <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl">
                          <div className="flex items-start">
                            <CheckCircleIcon className="w-5 h-5 text-blue-700 mt-0.5 mr-2" />
                            <p className="text-blue-800">{f.bullet}</p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                ))}
              </div>

              <div className="mt-14 text-center">
                <a
                    href="/detailed-overview"
                    className="inline-flex items-center px-6 py-3 rounded-xl bg-gray-50 border border-gray-200 hover:bg-gray-100 transition-colors"
                >
                  {t('features.detailButton1')} <ArrowRightIcon className="w-5 h-5 ml-2" />
                </a>
              </div>
            </div>
          </section>

          {/* Use cases */}
          <section className="py-24 bg-gray-50">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-14">
                <h2 className="text-4xl font-bold mb-4">{t('useCases.title')}</h2>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  {t('useCases.subtitle')}
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {useCases.map((u, idx) => (
                    <motion.div
                        key={u.title}
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.08 }}
                        className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100"
                    >
                      <u.icon className="w-10 h-10 text-blue-600 mb-4" />
                      <h3 className="text-xl font-semibold mb-2">{u.title}</h3>
                      <p className="text-gray-600">{u.description}</p>
                    </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-24 bg-white">
            <div className="max-w-4xl mx-auto px-6">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold mb-4">{t('faq.title')}</h2>
                <p className="text-gray-600">
                  {t('faq.subtitle')}
                </p>
              </div>

              <div className="space-y-4">
                {faqs.map((f, idx) => (
                    <motion.details
                        key={f.q}
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.03 }}
                        className="group bg-gray-50 border border-gray-100 rounded-2xl p-6"
                    >
                      <summary className="cursor-pointer list-none flex items-center justify-between">
                        <span className="text-lg font-semibold">{f.q}</span>
                        <span className="ml-4 text-gray-400 group-open:rotate-90 transition-transform">›</span>
                      </summary>
                      <p className="mt-4 text-gray-600 leading-relaxed">{f.a}</p>
                    </motion.details>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-24">
            <div className="max-w-5xl mx-auto px-6 text-center">
              <motion.div
                  initial={{ scale: 0.98, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  className="bg-gradient-to-r from-blue-600 to-green-500 p-12 rounded-3xl text-white shadow-xl"
              >
                <h2 className="text-4xl font-bold mb-5">{t('cta.title')}</h2>
                <p className="text-xl mb-8 max-w-2xl mx-auto">
                  {t('cta.subtitle')}
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <Button gray onClick={() => router.push("/signup")}>
                    {t('cta.cta')}
                  </Button>
                  <a
                      href="/how-it-works"
                      className="inline-flex items-center justify-center px-8 py-3 rounded-xl bg-white/10 border border-white/20 hover:bg-white/15 transition-colors"
                  >
                    {t('cta.learnMore')} <ArrowRightIcon className="w-5 h-5 ml-2" />
                  </a>
                </div>
              </motion.div>
            </div>
          </section>
          <SeoInternalLinks />
        </div>
      </>
  );
}

export async function getStaticProps(ctx) {
  const { getI18nPropsWithSeo } = await import('@/utils/i18n');
  return getI18nPropsWithSeo(ctx, 'affiliateMarketingSoftware', '/affiliate-marketing-software');
}
